import type { ReactNode } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

interface InspectionSummaryBadgeProps {
	label: ReactNode
	className: string
}

interface InspectionSummaryCardProps {
	accentClassName: string
	onClick?: () => void
	className?: string
	children: ReactNode
}

interface InspectionSummaryCardHeaderProps {
	badge?: ReactNode
	topMeta?: ReactNode
	className?: string
}

interface InspectionSummaryCardTitleProps {
	children: ReactNode
	className?: string
}

interface InspectionSummaryCardBodyProps {
	children: ReactNode
	className?: string
}

interface InspectionSummaryCardFieldProps {
	label: ReactNode
	value: ReactNode
	className?: string
}

interface InspectionSummaryCardAsideProps {
	children?: ReactNode
	className?: string
}

interface InspectionSummaryCardLoadingProps {
	accentClassName: string
	className?: string
}

export const InspectionSummaryBadge = ({
	label,
	className,
}: InspectionSummaryBadgeProps) => {
	return (
		<span
			className={cn(
				'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold',
				className,
			)}
		>
			{label}
		</span>
	)
}

export const InspectionSummaryCard = ({
	accentClassName,
	onClick,
	className,
	children,
}: InspectionSummaryCardProps) => {
	return (
		<Card
			className={cn(
				onClick ? 'cursor-pointer transition-shadow hover:shadow-md' : '',
				'gap-0 overflow-hidden border-l-5 py-0',
				accentClassName,
				className,
			)}
			{...(onClick
				? {
						onClick,
						onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault()
								onClick()
							}
						},
						role: 'button' as const,
						tabIndex: 0,
					}
				: {})}
		>
			{children}
		</Card>
	)
}

export const InspectionSummaryCardHeader = ({
	badge,
	topMeta,
	className,
}: InspectionSummaryCardHeaderProps) => {
	return (
		<div
			className={cn(
				'mb-1.5 flex items-center justify-between gap-2',
				className,
			)}
		>
			{badge ?? <div />}
			{topMeta}
		</div>
	)
}

export const InspectionSummaryCardBody = ({
	children,
	className,
}: InspectionSummaryCardBodyProps) => {
	return (
		<CardContent className={cn('flex-none px-3 py-2 sm:px-4', className)}>
			{children}
		</CardContent>
	)
}

export const InspectionSummaryCardTitle = ({
	children,
	className,
}: InspectionSummaryCardTitleProps) => {
	return (
		<h3
			className={cn(
				'my-4 line-clamp-1 text-base leading-snug text-kaefer-dark-grey',
				className,
			)}
		>
			{children}
		</h3>
	)
}

export const InspectionSummaryCardField = ({
	label,
	value,
	className,
}: InspectionSummaryCardFieldProps) => {
	return (
		<div className={cn('min-w-0 space-y-0.5', className)}>
			<span className="block text-xs leading-tight text-muted-foreground">
				{label}
			</span>
			<span className="block min-w-0 truncate overflow-hidden whitespace-nowrap text-sm text-kaefer-dark-grey">
				{value}
			</span>
		</div>
	)
}

export const InspectionSummaryCardAside = ({
	children,
	className,
}: InspectionSummaryCardAsideProps) => {
	if (!children) return null

	return <div className={cn('shrink-0', className)}>{children}</div>
}

export const InspectionSummaryCardLoading = ({
	accentClassName,
	className,
}: InspectionSummaryCardLoadingProps) => {
	return (
		<InspectionSummaryCard
			accentClassName={accentClassName}
			className={cn(className, 'h-40 max-w-sm')}
		>
			<InspectionSummaryCardBody className="py-2">
				<InspectionSummaryCardHeader
					badge={<Skeleton className="h-4 w-24 rounded-full" />}
					topMeta={<Skeleton className="h-3.5 w-16" />}
				/>

				<Skeleton className="my-3 h-5 w-10/12" />

				<div className="flex items-center justify-between gap-2 text-xs">
					<div className="min-w-0 flex-1 space-y-1.5">
						<Skeleton className="h-2.5 w-20" />
						<Skeleton className="h-3.5 w-32" />
						<Skeleton className="h-2.5 w-16" />
						<Skeleton className="h-3.5 w-28" />
					</div>
					<Skeleton className="h-5 w-10" />
				</div>
			</InspectionSummaryCardBody>
		</InspectionSummaryCard>
	)
}
